<script setup lang="ts">

import TestimonialVideoGrid from "~components/TestimonialVideoGrid.vue";

withDefaults(
  defineProps<{ bookCallUrl?: string }>(),
  {bookCallUrl: 'about:blank'}
)

import TutorSpecialists from "~components/TutorSpecialists.vue";

</script>

<template>
  <main>
    <section class="hero bg-primary text-light">
      <div class="container-fluid pt-3 pb-3">
        <img style="max-width: 40rem;" src="/assets/images/lyrics_learn1.png" alt="Logo">
        <div class="row justify-content-lg-around">
          <div class="col-lg-6 mb-7 mb-md-0">
            <div class="mb-5">
              <h1 class="text-outline-grey mb-5">Online Reading Tutoring for
                <nobr>K - 6</nobr>
              </h1>
              <p class="fs-3">
                We combine The Science of Reading with music to skyrocket growth, confidence, and a love for reading.
              </p>
              <ul class="fs-4">
                <li>Full one-on-one reading evaluations and customized instruction plan for every student.</li>
                <li>Reading specialists with Master’s Degrees, PhDs, and trained in The Science of Reading</li>
                <li>Guarantee of 1 to 3 grade levels of reading improvement</li>
              </ul>
            </div>
            <a class="btn cta-btn btn-light btn-transition" :href="bookCallUrl">
              Book a Call ❯
            </a>
          </div>
          <div class="hero-image-container col-lg-6" style="max-width: 30rem; flex: 0.5 1 20rem;">
            <img
              class="hero-image img-fluid"
              src="/assets/images/happy_girl.png"
              alt="Happy Girl Using L2L"
            >
          </div>
        </div>
      </div>

      <div class="shape shape-bottom zi-1">
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 1920 100.1">
          <path fill="#fff" d="M0,0c0,0,934.4,93.4,1920,0v100.1H0L0,0z"></path>
        </svg>
      </div>
    </section>

    <section class="classwallet">
      <div class="section-content drop-shadow-medium">
        <header>
          <h1>Lyrics2Learn’s tutoring and homeschool programs can be purchased with state ESA Funds.</h1>
        </header>

        <table class="states-available">
          <tr>
            <th>States currently eligible:</th>
            <td class="states-eligible">AL, AR, AZ, FL, ID, IN, MO, NC, NH, NY, SC, TX, UT, WY</td>
          </tr>
          <!-- No states in approval process at this time (2025-11-13)
          <tr>
            <th>States in approval process:</th>
            <td class="states-in-approval">NY</td>
          </tr>
          -->
        </table>

        <a href="/classwallet" class="learn-more btn btn-primary">Learn More</a>
      </div>
    </section>

    <section class="master-specialists mt-5">
      <div class="section-content">
        <div class="row">
          <div class="col-lg-6">
            <h1 class="text-primary">Master Specialists</h1>
            <ul>
              <li>
                Professional reading specialists with Master's degrees, Double Master's degrees, PhDs, and multiple
                certifications in programs aligned with The Science of Reading.
              </li>
              <li>
                By combining music, a love for reading, and research-based instructional strategies, our kids make
                1-3 grade levels of gains every 24 weeks.
              </li>
              <li>
                Learning to read is complex and can be frustrating for the majority of students. We offer our kids
                support, encouragement, and highly engaging sessions individualized to target the skills they are
                missing.
              </li>
              <li>
                By working with Universities, researchers, and collecting data, we constantly adjust instruction to help
                our kids improve; 98% of our students make gains of 1.5 grade levels or more.
              </li>
            </ul>
          </div>
          <div class="col-lg-6 text-center">
            <strong>Parent Reviews</strong>
            <TutorVideoCarousel/>
          </div>
        </div>
        <div class="text-center mt-5">
          <a class="btn cta-btn btn-primary btn-transition w-100 mx-auto" :href="bookCallUrl">
            Book a Call ❯
          </a>
        </div>
      </div>
    </section>

    <section class="how-it-works bg-dark text-white">
      <div class="section-content">
        <div class="player-container">
          <VimeoPopout :video-id="998888923" thumbnail="/images/homeschool/jeremy_story_and_innovation.png"/>
        </div>

        <div class="copy">
          <h1 style="color: rgb(245, 202, 153);">How it works</h1>

          <ol>
            <li>
              A one-on-one reading evaluation to determine holes in phonemic awareness, phonics, fluency,
              and 3 levels of comprehension.
            </li>
            <li>
              A fully customized instructional plan outlining what skills are mastered in every reading component,
              what is missing, and reading goals to excel.
            </li>
            <li>
              Two hours of research-based instruction and practice weekly,
              targeting missing skills and adapting instruction as needed.
            </li>
            <li>
              One-on-one parent Zooms every 12 weeks to go over progress.
            </li>
          </ol>

          <div class="text-center mt-5">
            <a class="btn cta-btn btn-light btn-transition" :href="bookCallUrl">
              Book a Call ❯
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="clear-communication">
      <div class="section-content">
        <div class="row">
          <div class="col-lg-6">
            <h1 style="color: rgb(0, 201, 167);">Clear Communication</h1>
            <p>
              Every 12 weeks, parents Zoom with a specialist to view their child’s progress in all reading components.
            </p>
            <strong>De-identified sample of a student’s progress summary</strong>
            <br>
            <b-img-lazy src="/images/tutor/improvement-table-sample.png" style="max-width: 25rem;" alt=""></b-img-lazy>
          </div>

          <div class="col-lg-6 text-center">
            <b-img-lazy
              src="/images/tutor/prescott-family.png"
              style="max-width: 25rem; border-radius: 0.5rem;"
              alt="Prescott Family"
            ></b-img-lazy>
            <div class="text-bold">Prescott Family</div>
            <p>
              We could not be more appreciative of this program! There is nothing else out there like it (believe me, we
              about tried them all) and the outcomes get better week to week! It’s worth it, to see your kid come into
              their own understanding and gain confidence is something I could never put into words.
            </p>
          </div>
        </div>
        <div class="text-center mt-5">
          <a class="btn cta-btn btn-primary btn-transition" :href="bookCallUrl">
            Book a Call ❯
          </a>
        </div>
      </div>
    </section>

    <section class="why-music bg-primary text-white">
      <div class="section-content">
        <div class="row">
          <div class="col-lg-6">
            <h1 class="text-white mt-10">Why Music?</h1>
            <ul>
              <li>Music makes fluency practice more FUN, &amp; more EFFECTIVE.</li>
              <li>Kids retain more information improving vocabulary, fluency and comprehension.</li>
              <li>Worksheets can take the love out of reading. Music heightens emotion, connection to text, and
                enjoyment.
              </li>
            </ul>
            <div class="cta text-center">
              <a class="btn cta-btn btn-light btn-transition" :href="bookCallUrl">
                Book a Call ❯
              </a>
            </div>
          </div>
          <div class="col-lg-6 text-center">
            <b-img-lazy src="/images/lefty-notes.png" style="max-height: 25rem;" alt=""></b-img-lazy>
          </div>
        </div>
      </div>
    </section>

    <section class="our-specialists">
      <h1 class="color-dark-red">Our Specialists</h1>
      <TutorSpecialists/>
      <div class="cta">
        <a class="btn cta-btn btn-primary btn-transition" :href="bookCallUrl">
          Book a Call ❯
        </a>
      </div>
    </section>

    <div id="success-stories" aria-hidden="true"></div>

    <section class="testimonials">
      <div class="section-content">
        <h1>Success Stories</h1>
      </div>
      <TestimonialVideoGrid/>
    </section>
  </main>
</template>

<style scoped lang="scss">
@import "../../../sass/theme.scss";

/********** General **********/
main {
  font-size: 1.25rem;
}

section {
  padding: 3rem 2rem;
}

section.hero {
  padding: 0;
}

.section-content {
  margin-left: auto;
  margin-right: auto;
}

.cta {
  padding: 1rem 0;
  text-align: center;
}

.cta-btn {
  margin: 1rem auto;
  min-width: 16rem;
  max-width: 16rem;
  font-weight: bold;
}

h1, h2, h3 {
  text-align: center;
}

/********** Hero **********/

section.hero {
  color: $light;
}

section.hero h1, section.hero h2 {
  color: $light;
}

/********** ClassWallet ("can be purchased with ESA") **********/

section.classwallet .section-content {
  padding: 1rem;
  border: 2px solid $gray-400;
  border-radius: 1rem;
  background-color: #FFFAF5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 20rem;
}

section.classwallet header {
  margin: 0;
}

section.classwallet header h1 {
  font-size: 1.75rem;
  color: $cs-l2l-dark-blue;
  border-radius: 1rem;
  padding: 0.25rem 1rem;
  margin: 0 0 1rem;
}

section.classwallet a.learn-more {
  margin: auto;
  height: 3.5rem;
  width: 15rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: bold;
}

table.states-available {
  max-width: 60rem;
  margin: 0 auto;
}

table.states-available tr {
  border-bottom: 1px solid $gray;
  border-top: 1px solid $gray;
}

table.states-available th {
  text-align: right;
  padding: 1rem 1rem 1rem 4rem;
}

table.states-available td {
  padding: 1rem 4rem 1rem 1rem;
  font-weight: bold;
}

table.states-available {
  color: $gray-800;
  font-size: 1.5rem;
}

td.states-eligible {
  color: $cs-l2l-dark-green;
}

td.states-in-approval {
  color: $cs-l2l-dark-purple;
}

.btn.learn-more {

}


/********** Master Specialists **********/

section.master-specialists ul {
  font-size: 1.2rem;
}

section.master-specialists ul li {
  color: #555;
  margin-top: 1rem;
}

/********** How It Works **********/

section.how-it-works {

  .section-content {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
  }

  .player-container {
    min-width: 20rem;
    max-width: 45%;
  }

  .copy {
    max-width: 45%;
  }

  li {
    color: #ccc;
    margin-top: 1rem;
  }
}

/********** Why Music **********/

section.why-music ul li {
  color: #eee;
  margin-top: 1rem;
}

/********** Our Specialists **********/

section.our-specialists .section-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

section.our-specialists .section-content > * {
  flex: 0 0 20rem;
}

section.our-specialists .profile-card {
  margin: 2rem;
}

</style>

